import React, { memo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ProfileCard from '@atlassian/jira-profilecard-next/src/ui/profilecard/sync-experiment.tsx';
import type { UserProfile$key } from '@atlassian/jira-relay/src/__generated__/UserProfile.graphql';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';

interface Props {
	dataRef: UserProfile$key;
}

const UserProfile = ({ dataRef }: Props): React.JSX.Element => {
	const {
		accountId,
		name: displayName,
		picture: avatarUrl,
	} = useFragment<UserProfile$key>(
		graphql`
			fragment UserProfile on User {
				accountId
				name
				picture
			}
		`,
		dataRef,
	);

	const props = { accountId, prepopulatedData: { fullName: displayName } };

	return isVisualRefreshEnabled() ? (
		<ProfileCard {...props}>
			<Box xcss={containerStyles} data-testId={`directory-user-profile-${accountId}`}>
				<Box xcss={avatarContainerStyles}>
					<Avatar src={avatarUrl} size="small" appearance="circle" />
				</Box>
				<Text color="color.text.subtle">{displayName}</Text>
			</Box>
		</ProfileCard>
	) : (
		<ProfileCard {...props}>
			<ContentContainer data-testId={`directory-user-profile-${accountId}`}>
				<AvatarContainer>
					<Avatar src={avatarUrl} size="small" appearance="circle" />
				</AvatarContainer>
				<TextContainer>{displayName}</TextContainer>
			</ContentContainer>
		</ProfileCard>
	);
};

const UserProfileMemoized = memo(UserProfile);

export { UserProfileMemoized as UserProfile };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	flex: '1 1 auto',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarContainer = styled.div({
	paddingRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 0,
	flex: '0 0 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	wordWrap: 'break-word',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const containerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	flex: '1 1 auto',
	width: '100%',
});

const avatarContainerStyles = xcss({
	paddingRight: 'space.100',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 0,
	flex: '0 0 auto',
});
